import { JOURNEYS_DIMENSIONS_WHITELIST } from '../../../../../../shared/constants/journeysDimensionsWhitelist';
import type { Status, TemplateOption } from '../../../types/journeyTypes';

import tabText from './journeyText/viewEditorTabs';

export const { TIME_PERIOD, BEHAVIOR } = tabText.DISMISSAL_SETTINGS;

export const TEXT_HAS_APP = 'OPEN';
export const TEXT_NO_APP = 'GET';

export const AMP_KEY = 'amp';
export const WEB_KEY = 'web';

export const MOBILE_KEY = 'mobile';

export const DEFAULT_KEY = MOBILE_KEY;

export const DESKTOP_KEY = 'desktop';

export const DEVICE_TYPES = ['tablet', 'mobile'];
export const DEVICE_OSS = ['ios', 'android'];

/**
 * This is required as an intermediate step before we calculate all
 * dimensions of the device dynamically. For now
 * we hard code the values for iPhone 7+ then use those proportions to
 * derive other height and padding requirements
 */
export const DEVICE_DEFAULT_PADDING = {
  top: 90.5,
  left: 21.5,
};

export const NAME_RULE_FIELD = 'title';
export const CAMPAIGN_RULE_FIELD = 'campaign';
export const CHANNEL_RULE_FIELD = 'channel';

export const DISMISS_BUTTON_CLASS = 'branch-banner-close';
export const DISMISS_TEXT_CLASS = 'branch-banner-continue';
export const DISMISS_TEXT_ID = 'branch-banner-close2';
export const DISMISS_TEXT_LANGUAGE_KEY = '$journeys_button_cancel';
export const DISMISSAL_TAB_CLASSES = [DISMISS_BUTTON_CLASS, DISMISS_TEXT_CLASS];

export const OPEN_APP_DEFAULT = false;

export const SECONDS_PER_TIME_UNIT = {
  hour: 60 * 60,
  day: 24 * 60 * 60,
  week: 7 * 24 * 60 * 60,
  month: 30 * 24 * 60 * 60,
  never: -1,
};

// This is an approximate date anyway, so we're going to use UTC
// and not worry about timezones
export const DATE_NEW_JOURNEY_FIELDS_ADDED = '2018-05-01T00:00:00Z';

export const JOURNEYS_ANALYTICS_FILTERS_LIST = JOURNEYS_DIMENSIONS_WHITELIST;

// NOTE: This is tied to `BannerType` in the journeyTypes file
// (if you change one make sure to change the other)
// If only flow could use variables this could be avoided :(
export const BANNER_TYPES = {
  BANNER: 'BANNER',
  INTERSTITIAL: 'INTERSTITIAL',
  HALFPAGE: 'HALFPAGE',
  BUTTON: 'BUTTON',
  BANNER_BOTTOM: 'BANNER_BOTTOM',
};

export const BANNER_LABELS = {
  BANNER: 'Banner',
  INTERSTITIAL: 'Interstitial Banner',
  HALFPAGE: 'Halfpage Banner',
  BUTTON: 'Button Banner',
  BANNER_BOTTOM: 'Bottom Banner',
};

export const TEMPLATE_TYPE_OPTIONS: TemplateOption[] = [
  {
    type: null,
    value: 'all',
    premium: null,
    label: 'all view types',
    availableFor: { [WEB_KEY]: 1 },
  },
  {
    type: BANNER_TYPES.BANNER_BOTTOM,
    value: 'banner bottom',
    premium: 0,
    label: 'standard bottom banner',
    availableFor: { [AMP_KEY]: 1 },
  },
  {
    type: BANNER_TYPES.BANNER,
    value: 'banner',
    premium: 0,
    label: 'smart banner',
    availableFor: { [WEB_KEY]: 1 },
  },
  {
    type: BANNER_TYPES.INTERSTITIAL,
    value: 'interstitial',
    label: 'interstitial',
    premium: 1,
    availableFor: { [WEB_KEY]: 1 },
  },
  {
    type: BANNER_TYPES.BUTTON,
    value: 'button',
    premium: 1,
    label: 'button',
    availableFor: { [WEB_KEY]: 1 },
  },
];

export const IMAGE_POSITION_LIST = [
  { id: 'top', label: 'Top' },
  { id: 'center', label: 'Center' },
  { id: 'bottom', label: 'Bottom' },
  { id: 'cover', label: 'Cover' },
];

export const TOGGLE_HEIGHT_LIST = [
  { id: 'vh', label: 'Relative' },
  { id: 'px', label: 'Absolute' },
];

export const TOGGLE_WIDTH_LIST = [
  { id: 'vw', label: 'Relative' },
  { id: 'px', label: 'Absolute' },
];

export const CONTENT_POSITION_LIST = [
  { id: 'top', label: 'Top', number: '4' },
  { id: 'center', label: 'Center', number: '24' },
  { id: 'bottom', label: 'Bottom', number: '49' },
  { id: 'custom', label: 'Custom' },
];

/* ------------------------Audience Editor - FILTERS--------------------------*/

export const QUERY_BUILDER_TYPES = {
  COMPLETED_EVENT: 'COMPLETED_EVENT',
  REFERRER: 'REFERRER',
  INITIAL_REFERRER: 'INITIAL_REFERRER',
  VISITED_SITE: 'VISITED_SITE',
  VISITED_APP: 'VISITED_APP',
  HAS_APP: 'HAS_APP',
  METADATA: 'METADATA',
  REGION: 'REGION',
  EMAIL: 'EMAIL',
  ADS: 'ADS',
  VALUE_GREATER_THAN: 'VALUE_GREATER_THAN',
  VALUE_LESS_THAN: 'VALUE_LESS_THAN',
  VALUE_EQUALS: 'VALUE_EQUALS',
  VALUE_BETWEEN: 'VALUE_BETWEEN',
  VALUE_WITHIN: 'VALUE_WITHIN',
  NO_BOUNDS: 'NO_BOUNDS',
  EXACTLY_MATCHING: 'EXACTLY_MATCHING',
  NOT_MATCHING: 'NOT_MATCHING',
  STARTS_WITH: 'STARTS_WITH',
  NOT_STARTS_WITH: 'NOT_STARTS_WITH',
  CONTAINING: 'CONTAINING',
  NOT_CONTAINING: 'NOT_CONTAINING',
  QUERY_PARAM: 'QUERY_PARAM',
  NOT_QUERY_PARAM: 'NOT_QUERY_PARAM',
  DOMAIN_MATCHING: 'DOMAIN_MATCHING',
  DOMAIN_NOT_MATCHING: 'DOMAIN_NOT_MATCHING',
  VIEW_DISMISS: 'VIEW_DISMISS',
  JOURNEY_INTERACTION: 'JOURNEY_INTERACTION',
  ANY: 'ANY',
  true: 'true',
  false: 'false',
};

export const DESKTOP_FILTER_WHITELIST_SET = new Set([]);

export const URL_OPTIONS = [
  { value: QUERY_BUILDER_TYPES.EXACTLY_MATCHING, label: 'exactly matching' },
  { value: QUERY_BUILDER_TYPES.NOT_MATCHING, label: 'not matching' },
  { value: QUERY_BUILDER_TYPES.STARTS_WITH, label: 'starting with' },
  { value: QUERY_BUILDER_TYPES.NOT_STARTS_WITH, label: 'not starting with' },
  { value: QUERY_BUILDER_TYPES.QUERY_PARAM, label: 'with query param key' },
  {
    value: QUERY_BUILDER_TYPES.NOT_QUERY_PARAM,
    label: 'without query param key',
  },
  { value: QUERY_BUILDER_TYPES.DOMAIN_MATCHING, label: 'whose domain matches' },
  {
    value: QUERY_BUILDER_TYPES.DOMAIN_NOT_MATCHING,
    label: 'whose domain does not match',
  },
  { value: QUERY_BUILDER_TYPES.CONTAINING, label: 'whose path contains' },
  {
    value: QUERY_BUILDER_TYPES.NOT_CONTAINING,
    label: 'whose path does not contain',
  },
];

export const POS_KEY_VALUE_MATCH_OPTIONS = [
  { value: QUERY_BUILDER_TYPES.EXACTLY_MATCHING, label: 'whose value matches' },
  { value: QUERY_BUILDER_TYPES.STARTS_WITH, label: 'whose value starts with' },
  { value: QUERY_BUILDER_TYPES.ANY, label: 'whose value matches anything' },
];

export const KEY_VALUE_MATCH_OPTIONS = POS_KEY_VALUE_MATCH_OPTIONS.concat([
  {
    value: QUERY_BUILDER_TYPES.NOT_MATCHING,
    label: 'whose value does not match',
  },
  {
    value: QUERY_BUILDER_TYPES.NOT_STARTS_WITH,
    label: 'whose value does not start with',
  },
]);

export const HAS_APP_OPTIONS = [
  { value: QUERY_BUILDER_TYPES.true, label: 'true' },
  { value: QUERY_BUILDER_TYPES.false, label: 'false' },
];

export const OPERATOR_OPTIONS = [
  {
    value: QUERY_BUILDER_TYPES.VALUE_GREATER_THAN,
    label: 'more than or equal to',
    range: false,
  },
  {
    value: QUERY_BUILDER_TYPES.VALUE_LESS_THAN,
    label: 'less than or equal to',
    range: false,
  },
  { value: QUERY_BUILDER_TYPES.VALUE_EQUALS, label: 'exactly', range: false },
  {
    value: QUERY_BUILDER_TYPES.VALUE_BETWEEN,
    label: 'between (inclusive)',
    range: true,
  },
  { value: QUERY_BUILDER_TYPES.VALUE_WITHIN, label: 'within', range: false },
];

export const REGION_OPTIONS = [
  { value: QUERY_BUILDER_TYPES.EXACTLY_MATCHING, label: 'in' },
  { value: QUERY_BUILDER_TYPES.NOT_MATCHING, label: 'not in' },
];

export const INTERACTION_OPTIONS = [
  { value: 'dismiss', label: 'dismiss' },
  /* Will add in future when EO topics are ready:
                                  { value: 'click', label: 'click' },
                                  { value: 'view', label: 'view' }
                                  */
];

export const INTERACTION_TYPE_DEFAULT = 'dismiss';
export const INTERACTION_PERIOD_DEFAULT = SECONDS_PER_TIME_UNIT.week; // in seconds

/* -----------------------View Editor - DISMISS-------------------------*/

export const DISMISS_DROPDOWN_OPTIONS = [
  { label: '1 hour', value: 'hour' },
  { label: '1 day', value: 'day' },
  { label: '1 week', value: 'week' },
  { label: '1 month', value: 'month' },
  { label: 'Never Again', value: 'never' },
  { label: 'Custom', value: 'custom' },
];

// For AMPs we cannot control dismissal behavior, therefore we want to
// represent 'Until next pageview' in the dropdown as that is the behavior AMP uses.
// NOTE: We currently don't support 'Until next pageview' for non-AMP Journeys
export const AMP_DROPDOWN_OPTION = {
  label: 'Until next pageview',
  value: 'untilNext',
};

export const DISMISS_PERIOD_DEFAULT = INTERACTION_PERIOD_DEFAULT;
export const DISMISS_CUSTOM_PERIOD_DEFAULT = SECONDS_PER_TIME_UNIT.day;

export const TIME_PERIOD_RADIO_OPTIONS = [
  { id: 'dismiss-this-journey', label: TIME_PERIOD.RADIO_ITEMS.LOCAL },
  { id: 'dismiss-all-journeys', label: TIME_PERIOD.RADIO_ITEMS.GLOBAL },
];

export const BEHAVIOR_RADIO_OPTIONS = [
  { id: 'close-on-dismiss', label: BEHAVIOR.RADIO_ITEMS.CLOSE },
  { id: 'redirect-on-dismiss', label: BEHAVIOR.RADIO_ITEMS.REDIRECT },
];

export const OVERLAY_STYLE_DEFAULT = {
  background: 'black',
  opacity: 0.5,
  position: 'fixed',
  pointerEvents: 'none',

  left: 0,
  top: 0,
  width: 0,
  height: 0,

  zIndex: 1000000,
  border: '1px dashed yellow',
  boxSizing: 'border-box',
};

export const JOURNEY_STATUS = {
  ACTIVE: 'ACTIVE' as Status,
  STOPPED: 'STOPPED' as Status,
  SCHEDULED: 'SCHEDULED' as Status,
  DRAFT: 'DRAFT' as Status,
  ARCHIVED: 'ARCHIVED' as Status,
  INACTIVE: 'INACTIVE' as Status,
  PAUSED: 'PAUSED' as Status,
};

export const JOURNEY_DEFAULT_PRIORITY = 999;

export const JOURNEY_TEMPLATE_FRAME_DEFAULT_DIMENSION = {
  width: 280,
  height: 400,
};

export const JOURNEY_AUDIENCE_PLATFORM_DISPLAY_VALUE = {
  web: 'Mobile Web',
  amp: 'AMP Web',
};

export const JOURNEY_AUDIENCE_DEVICE_DISPLAY_VALUE = {
  ios: {
    mobile: 'iOS Mobile',
    tablet: 'iOS Tablet',
  },
  android: {
    mobile: 'Android Mobile',
    tablet: 'Android Tablet',
  },
};

export const JOURNEYS_PAGE_TITLE = 'Journeys';

export const ERROR_SAVING_JOURNEY_MSG = 'Your Journey could not be saved. Please check your entries and try again.';
export const ERROR_JOURNEY_EMPTY_NAME = "Journey name can't be empty.";
export const ERROR_JOURNEY_EMPTY_USER_DEVICE = 'Must select at least one user device group to target.';
export const ERROR_JOURNEY_EMPTY_PLATFORM = 'Must select at least one platform to target.';

export const RULEFIELDS_ERROR_MESSAGES = {
  EMPTY: 'Please fill out this field.',
  UNIQUE: 'Journey name is already in use.',
  CHARACTERS: 'Some characters (ex: \\, &, /, <, >, | ) are not allowed',
};

export const DEFAULT_CAMPAIGN_OBJECTIVE = 'acquisition';

export const DEFAULT_DYNAMIC_OPTIMIZATION = false;
